<template>
  <div
    class="hero-banner relative w-full overflow-hidden"
    :class="{ 'hero-banner--has-image': hasBanner && bannerImageUrl }"
    :style="bannerStyles"
  >
    <!-- Dark overlay for readability -->
    <div class="hero-overlay absolute inset-0"></div>

    <!-- Content -->
    <div
      class="hero-content relative z-10 px-4 sm:px-6 py-10 sm:py-12 md:py-20 max-w-4xl"
    >
      <!-- Event date — driven entirely by the admin's "Show dates"
           toggle on the banner element (banner.options.isDatesEnabled). -->
      <span v-if="showDate && eventDateLabel" class="hero-date">
        <i class="far fa-calendar-alt" aria-hidden="true"></i>
        {{ eventDateLabel }}
      </span>

      <!-- Event title -->
      <h1 class="hero-title text-white font-bold mt-3 mb-3 leading-tight">
        {{ cleanTitle }}
      </h1>

      <!-- Description — only when the admin has added a banner element -->
      <p
        v-if="hasBanner && data && data.about"
        class="hero-description text-white text-sm md:text-base mb-6 max-w-2xl leading-relaxed"
        style="opacity: 0.88"
      >
        {{ truncatedDescription }}
      </p>

      <!-- Action buttons -->
      <div class="hero-actions flex flex-wrap gap-3 mt-6">
        <!-- Register Now — only inside the admin-configured banner -->
        <a
          v-if="hasBanner && registrationUrl"
          :href="registrationUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="hero-btn hero-btn--primary"
        >
          {{ $t('Views.Lobby.HeroBanner.registerNow') }}
        </a>
      </div>

      <!-- Banner sponsors — the "Top sponsors" the admin attached to the
           banner element in the lobby builder
           (banner.options.topSponsorOptions). Rendered in the admin's
           order and scaled by their "Size" slider, exposed to the CSS
           below as `--banner-sponsor-scale`. -->
      <SponsorsDataProvider
        v-if="bannerSponsorIds.length"
        v-slot="{ sponsors }"
      >
        <div
          v-if="bannerSponsors(sponsors).length"
          class="hero-sponsors"
          :style="{ '--banner-sponsor-scale': bannerSponsorScale }"
        >
          <button
            v-for="sponsor in bannerSponsors(sponsors)"
            :key="`banner-sponsor-${sponsor.id || sponsor.Id}`"
            type="button"
            class="hero-sponsors__tile"
            @click="onSponsorClick(sponsor)"
          >
            <img
              v-if="sponsor.logoUrl"
              :src="sponsor.logoUrl"
              :alt="
                sponsor.name ||
                sponsor.companyName ||
                $t('Views.Lobby.HeroBanner.sponsor')
              "
              class="hero-sponsors__logo"
            />
            <span v-else class="hero-sponsors__name">
              {{
                sponsor.name ||
                sponsor.companyName ||
                $t('Views.Lobby.HeroBanner.sponsor')
              }}
            </span>
          </button>
        </div>
      </SponsorsDataProvider>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { intlLocale } from '@/i18n';
import SponsorInfoModal from '@/components/SponsorInfoModal';
import {
  resolveTopSponsors,
  topSponsorIdsOf,
  topSponsorOptionsOf,
  topSponsorScale
} from '@/helpers/top-sponsors';

export default {
  name: 'HeroBanner',
  props: {
    data: {
      type: Object,
      default: null
    },
    features: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapGetters('CurrentEvent', ['getEvent', 'lobbyElements']),
    ...mapGetters('Agendas', ['getSessions']),
    liveSession() {
      // Find a session that is currently live (now between start and end).
      // Used to surface the "Join Live Session" CTA in the hero banner.
      const sessions = this.getSessions || [];
      if (!sessions.length) return null;
      const now = Date.now();
      return (
        sessions.find((s) => {
          if (!s || !s.startDate || !s.endDate) return false;
          const start = new Date(s.startDate).getTime();
          const end = new Date(s.endDate).getTime();
          return now >= start && now <= end;
        }) || null
      );
    },
    bannerElement() {
      return this.lobbyElements?.find((x) => x && x.id === 'banner') || null;
    },
    bannerOptions() {
      return this.bannerElement?.options ?? {};
    },
    bannerText() {
      return this.bannerOptions.text || '';
    },
    bannerImageUrl() {
      return this.bannerOptions.fileUrl || '';
    },
    // True when the admin has actually added a banner element to the
    // lobby page (regardless of whether they filled in image/text).
    // Drives the conditional "rich hero" vs "minimal hero" layout.
    hasBanner() {
      return !!this.bannerElement;
    },
    // The banner's `{ size, elements }` top-sponsor block, as saved by
    // the lobby builder. Empty object when the admin never configured it.
    bannerTopSponsorOptions() {
      return this.hasBanner ? topSponsorOptionsOf(this.bannerOptions) : {};
    },
    // Ids the admin picked, in their order — known before the sponsor
    // list has loaded, so it can drive the fetch below.
    bannerSponsorIds() {
      return topSponsorIdsOf(this.bannerTopSponsorOptions);
    },
    // The admin's "Size" slider for the banner sponsors, as a scale factor.
    bannerSponsorScale() {
      return topSponsorScale(this.bannerTopSponsorOptions.size);
    },
    registrationUrl() {
      return (
        this.data?.registrationUrl || this.getEvent?.registrationUrl || null
      );
    },
    isLive() {
      if (!this.data?.startDate || !this.data?.endDate) return false;
      const now = Date.now();
      const start = new Date(this.data.startDate).getTime();
      const end = new Date(this.data.endDate).getTime();
      return now >= start && now <= end;
    },
    // Driven entirely by the admin-side "Show dates" toggle on the
    // banner element (`banner.options.isDatesEnabled`). When the
    // banner element isn't configured at all, fall back to false so
    // we don't surface a date the admin never opted in to.
    showDate() {
      return !!(this.bannerOptions && this.bannerOptions.isDatesEnabled);
    },
    eventDateLabel() {
      const formatOne = (raw) => {
        if (!raw) return '';
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString(intlLocale(), {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      };
      const start = formatOne(this.data && this.data.startDate);
      const end = formatOne(this.data && this.data.endDate);
      if (!start) return '';
      if (end && end !== start) return `${start} – ${end}`;
      return start;
    },
    truncatedDescription() {
      const text = this.data?.about || '';
      return text.length > 200 ? text.substring(0, 200) + '…' : text;
    },
    cleanTitle() {
      // Prefer the admin-supplied banner text (when a banner element
      // exists and they typed a title for it); otherwise fall back to
      // the event's own name. Strip any HTML so the heading renders
      // as plain text.
      const raw =
        (this.hasBanner && this.bannerText) ||
        (this.data && this.data.eventName) ||
        this.getEvent?.Title ||
        '';
      const tmp = document.createElement('div');
      tmp.innerHTML = raw;
      return tmp.textContent || tmp.innerText || raw;
    },
    bannerStyles() {
      // Banner image only paints when the admin has added a banner
      // element with an uploaded file. Otherwise the hero falls back
      // to the brand colour fill — no third-party stock image.
      const styles = {
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#0a3d35',
        minHeight: '320px'
      };
      if (this.hasBanner && this.bannerImageUrl) {
        // Quoted so uploaded file names with spaces or parentheses
        // (stored unencoded) still form a valid CSS url().
        const safeUrl = String(this.bannerImageUrl)
          .trim()
          .replace(/[\r\n]/g, '')
          .replace(/["\\]/g, '\\$&');
        styles.backgroundImage = `url("${safeUrl}")`;
      }
      return styles;
    }
  },
  methods: {
    // The resolved sponsor objects, in the admin's order. Empty while the
    // provider is still loading, or when every selected sponsor has since
    // been deleted.
    bannerSponsors(sponsors) {
      if (!this.bannerSponsorIds.length) return [];
      return resolveTopSponsors(this.bannerTopSponsorOptions, sponsors);
    },
    // Same affordance as the sponsor sections further down the lobby.
    onSponsorClick(sponsor) {
      if (!sponsor) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SponsorInfoModal,
        { sponsor },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    onShare() {
      if (navigator.share) {
        navigator
          .share({
            title: this.data?.eventName || '',
            url: window.location.href
          })
          .catch(() => {});
      } else {
        navigator.clipboard?.writeText(window.location.href).catch(() => {});
      }
    },
    joinLiveSession() {
      const session = this.liveSession;
      if (!session) return;
      // Persist the live session in the Stream store so the LiveStream
      // page picks up the URL/title/id immediately on mount, mirroring
      // Program.vue's joinSession flow.
      this.$store.commit('Stream/setStreamUrl', session.liveUrl || '');
      this.$store.commit('Stream/setSessionId', session.id);
      this.$store.commit('Stream/setStreamTitle', session.title || '');
      this.$router.push({
        name: 'live-stream',
        query: { session: session.id }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.hero-banner {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    min-height: 420px;
    justify-content: flex-start;
  }
}

.hero-title {
  font-size: 28px;
  line-height: 1.15;

  @media (min-width: 640px) {
    font-size: 34px;
  }
  @media (min-width: 1024px) {
    font-size: 44px;
  }
}

/* Mobile: center the hero content horizontally */
@media (max-width: 640px) {
  .hero-content {
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .hero-description {
    max-width: 100%;
  }

  .hero-actions {
    justify-content: center;
  }
}

.hero-overlay {
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0.65) 0%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0.15) 100%
  );
}

// Hero date pill — promoted from the navbar so the date sits with
// the event title where it's relevant, not on every page.
.hero-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(2px);

  i {
    font-size: 12px;
    opacity: 0.9;
  }
}

// ── Banner sponsors ────────────────────────────────────────────────
// Light tiles over the hero image so logos stay legible whatever the
// admin uploaded. Tile geometry scales with the banner's "Size" slider
// (60–100%), matching the `--card-size` scaling the `tappin-banner`
// web component applies in the lobby builder preview.
.hero-sponsors {
  --banner-sponsor-scale: 1;

  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;

  &__tile {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: calc(150px * var(--banner-sponsor-scale));
    height: calc(84px * var(--banner-sponsor-scale));
    padding: 10px 16px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.92);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 14px rgba(0, 0, 0, 0.25);
    }

    &:focus-visible {
      outline: 2px solid #ffffff;
      outline-offset: 2px;
    }
  }

  &__logo {
    max-height: calc(60px * var(--banner-sponsor-scale));
    max-width: 100%;
    object-fit: contain;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    text-align: center;
  }

  @media (max-width: 640px) {
    justify-content: center;
    gap: 10px;

    &__tile {
      min-width: calc(120px * var(--banner-sponsor-scale));
      height: calc(70px * var(--banner-sponsor-scale));
    }
  }
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
  flex-shrink: 0;
}

.hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  text-decoration: none;
  border: none;
  outline: none;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  &--primary {
    background-color: var(--color-primary, #047873);
    color: #ffffff;

    &:hover {
      background-color: var(--color-primary-hover, #036663);
      opacity: 1;
    }
  }

  &--outline {
    background: transparent;
    border: 1.5px solid rgba(255, 255, 255, 0.75);
    color: #ffffff;
  }

  &--live {
    background-color: #16a34a;
    color: #ffffff;
    padding: 7px 14px;
    box-shadow: 0 3px 10px rgba(22, 163, 74, 0.3);

    &:hover {
      background-color: #15803d;
      opacity: 1;
    }

    .live-dot {
      width: 7px;
      height: 7px;
      animation: hero-live-pulse 1.4s ease-in-out infinite;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.2);
    }
  }
}

@keyframes hero-live-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.7;
  }
}
</style>
